/**
 * Saved formations — a coach's own starting setups, kept apart from the board.
 *
 * A saved formation records every piece on one phase: each player's team,
 * position, number and renamed position (if any), where they stand, and the
 * other pieces (the ball). Recalling it resets the play to that setup, as the
 * pack's built-in formations do.
 *
 * Coaches build these up over seasons, so the stored format must outlive every
 * release. The rules, pinned by `test/formations.test.ts` and its frozen
 * fixtures:
 *
 *  - Every formation carries `formationVersion`. A newer app always reads an
 *    older formation, through the migration chain below.
 *  - Unknown fields are ignored, never a reason to reject: a formation written
 *    by a newer app still loads in an older one as far as it can.
 *  - Pieces are keyed on stable ids — team, role, type — never on display
 *    names, which coaches can edit.
 *  - A formation this app cannot read (a newer version) is skipped, never
 *    deleted.
 */

import type { Entity, PlayDocument } from './document.ts';
import { FIELD_INSET } from './document.ts';
import { clampToField, type Vec2 } from './geometry.ts';
import type { Id, IdFactory } from './ids.ts';
import { SchemaError } from './serialization.ts';
import { alongRoute, type Measure } from './lanes.ts';
import type { PoseMap } from './timeline.ts';

/** Bump only with a migration from the previous version, and a new frozen fixture. */
export const FORMATION_VERSION = 1;

export interface SavedPlayer {
  readonly teamId: string;
  /** Absent for a player added without a position. */
  readonly roleId?: string;
  /** The coach's own name for the position, when they renamed it. */
  readonly roleLabel?: string;
  /** The player's number. */
  readonly label: string;
  readonly at: Vec2;
}

/** Any other piece, e.g. the ball. */
export interface SavedProp {
  readonly typeId: string;
  readonly label?: string;
  readonly at: Vec2;
}

export interface SavedFormation {
  readonly formationVersion: number;
  readonly id: Id;
  readonly name: string;
  readonly sportPackId: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly players: readonly SavedPlayer[];
  readonly props: readonly SavedProp[];
}

/**
 * Captures the pieces on one phase as a formation. Players are the pieces with
 * a team; everything else is a prop. Pieces not on that phase are left out.
 */
export function captureFormation(
  doc: PlayDocument,
  keyframeId: Id,
  options: { readonly id: Id; readonly name: string; readonly now: number; readonly createdAt?: number },
): SavedFormation {
  const frame = doc.keyframes.find((k) => k.id === keyframeId) ?? doc.keyframes[0]!;
  const players: SavedPlayer[] = [];
  const props: SavedProp[] = [];
  for (const entity of doc.entities) {
    const at = frame.positions[entity.id];
    if (!at) continue;
    if (entity.teamId !== undefined) {
      players.push({
        teamId: entity.teamId,
        ...(entity.roleId !== undefined ? { roleId: entity.roleId } : {}),
        ...(entity.roleLabel !== undefined ? { roleLabel: entity.roleLabel } : {}),
        label: entity.label,
        at,
      });
    } else {
      props.push({ typeId: entity.typeId, ...(entity.label ? { label: entity.label } : {}), at });
    }
  }
  return {
    formationVersion: FORMATION_VERSION,
    id: options.id,
    name: options.name.trim(),
    sportPackId: doc.sportPackId,
    createdAt: options.createdAt ?? options.now,
    updatedAt: options.now,
    players,
    props,
  };
}

/**
 * Resets the play to a saved formation: its pieces on a single phase, with no
 * drawings — as recalling a built-in formation does. The board's name, id and
 * view are kept.
 */
export function applySavedFormation(
  doc: PlayDocument,
  formation: SavedFormation,
  playerTypeId: string,
  newId: IdFactory,
  now: number,
): PlayDocument {
  const entities: Entity[] = [];
  const positions: Record<Id, Vec2> = {};
  for (const player of formation.players) {
    const id = newId('e');
    entities.push({
      id,
      typeId: playerTypeId,
      teamId: player.teamId,
      ...(player.roleId !== undefined ? { roleId: player.roleId } : {}),
      ...(player.roleLabel !== undefined ? { roleLabel: player.roleLabel } : {}),
      label: player.label,
    });
    positions[id] = clampToField(player.at, FIELD_INSET);
  }
  for (const prop of formation.props) {
    const id = newId('e');
    entities.push({ id, typeId: prop.typeId, label: prop.label ?? '' });
    positions[id] = clampToField(prop.at, FIELD_INSET);
  }
  return { ...doc, entities, shapes: [], keyframes: [{ id: newId('kf'), positions }], updatedAt: now };
}

/* ------------------------------------------------------------------ */
/* Gliding into a recalled formation                                   */
/* ------------------------------------------------------------------ */

/** What makes a piece the same piece across a recall: its team and number, or its type and label. */
function pieceKey(entity: Entity): string {
  return entity.teamId !== undefined ? `team:${entity.teamId}:${entity.label}` : `type:${entity.typeId}:${entity.label}`;
}

/**
 * Where each piece of a recalled formation starts its glide: from where the
 * same piece — same team and number, or same type — stood before. A piece with
 * no match is left out, and simply appears at its spot.
 */
export function carryOverPoses(
  previous: readonly Entity[],
  previousPoses: PoseMap,
  next: readonly Entity[],
): PoseMap {
  const stood = new Map<string, Vec2>();
  for (const entity of previous) {
    const at = previousPoses[entity.id];
    if (at && !stood.has(pieceKey(entity))) stood.set(pieceKey(entity), at);
  }
  const from: Record<Id, Vec2> = {};
  for (const entity of next) {
    const at = stood.get(pieceKey(entity));
    if (at) from[entity.id] = at;
  }
  return from;
}

/**
 * The board `elapsed` ms into a glide: every piece moving in a straight line
 * from where it started to where it belongs, at the one pace, arriving when
 * its own distance is covered. A piece with no start is already there.
 */
export function glidePoses(
  from: PoseMap,
  to: PoseMap,
  elapsed: number,
  measure: Measure,
): { poses: PoseMap; done: boolean } {
  const poses: Record<Id, Vec2> = {};
  let done = true;
  for (const [id, target] of Object.entries(to)) {
    const start = from[id];
    if (!start) {
      poses[id] = target;
      continue;
    }
    const step = alongRoute([start, target], elapsed, measure);
    poses[id] = step.at;
    if (step.fraction < 1) done = false;
  }
  return { poses, done };
}

/* ------------------------------------------------------------------ */
/* Reading stored formations                                           */
/* ------------------------------------------------------------------ */

type Json = Record<string, unknown>;
type Migration = (input: Json) => Json;

/**
 * Each entry lifts a formation from version `n` to `n + 1`. Empty today:
 * version 1 is the first. When the format changes, add the migration here,
 * bump `FORMATION_VERSION`, and freeze a fixture of the old version in
 * `test/fixtures/` so the migration is tested forever.
 */
const MIGRATIONS: Readonly<Record<number, Migration>> = {};

/** Reads a stored formation, migrating it to the current version. Throws `SchemaError` if it can't. */
export function readFormation(input: unknown): SavedFormation {
  if (!isObject(input)) throw new SchemaError('A formation must be a JSON object');
  let current: Json = input;
  let version = asNumber(current['formationVersion'], 'formationVersion');
  if (version > FORMATION_VERSION) {
    throw new SchemaError(`Formation version ${version} is newer than this app reads (${FORMATION_VERSION})`);
  }
  while (version < FORMATION_VERSION) {
    const migrate = MIGRATIONS[version];
    if (!migrate) throw new SchemaError(`No migration from formation version ${version}`);
    current = migrate(current);
    version += 1;
  }

  return {
    formationVersion: FORMATION_VERSION,
    id: asString(current['id'], 'id'),
    name: asString(current['name'], 'name'),
    sportPackId: asString(current['sportPackId'], 'sportPackId'),
    createdAt: asNumber(current['createdAt'], 'createdAt'),
    updatedAt: asNumber(current['updatedAt'], 'updatedAt'),
    players: asArray(current['players'], 'players').map(readPlayer),
    props: asArray(current['props'] ?? [], 'props').map(readProp),
  };
}

function readPlayer(raw: unknown): SavedPlayer {
  if (!isObject(raw)) throw new SchemaError('Expected a player object');
  return {
    teamId: asString(raw['teamId'], 'player.teamId'),
    ...(typeof raw['roleId'] === 'string' ? { roleId: raw['roleId'] } : {}),
    ...(typeof raw['roleLabel'] === 'string' && raw['roleLabel'] !== '' ? { roleLabel: raw['roleLabel'] } : {}),
    label: typeof raw['label'] === 'string' ? raw['label'] : '',
    at: asVec(raw['at'], 'player.at'),
  };
}

function readProp(raw: unknown): SavedProp {
  if (!isObject(raw)) throw new SchemaError('Expected a prop object');
  return {
    typeId: asString(raw['typeId'], 'prop.typeId'),
    ...(typeof raw['label'] === 'string' && raw['label'] !== '' ? { label: raw['label'] } : {}),
    at: asVec(raw['at'], 'prop.at'),
  };
}

function isObject(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asArray(value: unknown, field: string): unknown[] {
  if (!Array.isArray(value)) throw new SchemaError(`Expected "${field}" to be an array`);
  return value;
}

function asString(value: unknown, field: string): string {
  if (typeof value !== 'string') throw new SchemaError(`Expected "${field}" to be a string`);
  return value;
}

function asNumber(value: unknown, field: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new SchemaError(`Expected "${field}" to be a finite number`);
  }
  return value;
}

function asVec(value: unknown, field: string): Vec2 {
  if (!isObject(value)) throw new SchemaError(`Expected "${field}" to be a point`);
  return { x: asNumber(value['x'], `${field}.x`), y: asNumber(value['y'], `${field}.y`) };
}
